import { defineStore } from "pinia";
import events, { SubmitEventForReviewRequest } from "../Api/events";
import {
    EVENT_VISIBILITY,
    Event,
    EventMetadata,
    EventStatus,
    EventType,
    Visibility,
    FullEvent,
    ValidationErrorsMap,
} from "../Common/types";
import translatorMixin from "../Common/translatorMixin";

export type EventTypeObject = {
    key: EventType;
    label: string;
};

export function convertEventTypesToObjects(eventTypes: EventType[]): EventTypeObject[] {
    return eventTypes.map(et => ({ key: et, label: translatorMixin.methods.translate("event_type." + et) || et }));
}

export const useEditEventStore = defineStore("editEvent", {
    state: () => ({
        eventId: 0,
        organizerPhone: null as string | null,
        metadata: {
            canChangeStatus: true,
        } as EventMetadata,
        event: {
            id: 0,
            eventDate: {
                startDate: "",
                endDate: "",
            },
            status: 0,
            visibility: EVENT_VISIBILITY.None,
            country: null,
            fullName: "",
            shortName: "",
            urlSlug: "",
            place: "",
            thumbnailImage: null,
            backgroundImage: null,
            registrationStart: "",
            registrationEnd: "",
            internalInformation: null,
            externalInformation: null,
            translation: null,
            registrationStages: [],
            timezone: "UTC",
            geoLocation: null,
            contactInfo: null,
        } as Event,
        eventTypes: [] as EventTypeObject[],
        validationErrors: {} as ValidationErrorsMap,
        parsedMarkdown: "",
        changed: false,
        initEventStartDate: null as { name: string; value: string } | null,
        urlSlugDefinedForDraft: false,
    }),

    actions: {
        updateEvent(newEvent: Event) {
            this.event = newEvent;
            this.changed = false;

            if (newEvent.geoLocation && newEvent.geoLocation.indexOf(",") > 0) {
                const [lat, lng] = newEvent.geoLocation.split(",");
                // @ts-expect-error - Adding latitude and longitude dynamically TODO: Get rid of it, only contact model should maintain such data
                this.event.latitude = lat;
                // @ts-expect-error - Adding latitude and longitude dynamically
                this.event.longitude = lng;
            }
        },

        setEventTypes(eventTypes: EventTypeObject[]) {
            this.eventTypes = eventTypes;
            this.changed = true;
        },

        setEventProperty(payload: { name: string; value: unknown }) {
            this.event[payload.name] = payload.value;
            this.changed = true;
        },

        setEventDate(payload: { name: string; value: string }) {
            // Set init date only once: by default we have null, if so then we set it for the first time
            // Later this date can be compared to see if user picked different value and based on that apply same date to end event date.
            if (!this.initEventStartDate && payload.name === "startDate") {
                this.initEventStartDate = { name: "startDate", value: this.event.eventDate.startDate ?? "" };
            }

            this.event.eventDate[payload.name] = payload.value;
            this.changed = true;
        },

        fetchFullEvent(): Promise<FullEvent> {
            return events.getAdminEvent(this.eventId).then(fullEvent => {
                this.initEventStartDate = null;
                this.urlSlugDefinedForDraft = fullEvent.event.urlSlug === "draft";
                this.setEventTypes(convertEventTypesToObjects(fullEvent.eventTypes));
                this.updateEvent(fullEvent.event);
                if (fullEvent.metadata) {
                    this.metadata = fullEvent.metadata;
                }
                return fullEvent;
            });
        },

        saveEvent(eventData: Event, eventTypesToSave: EventType[]): Promise<FullEvent> {
            const fullEventUpdate: FullEvent = {
                event: eventData,
                eventTypes: eventTypesToSave,
            };

            return events
                .updateEventData(this.eventId, fullEventUpdate)
                .then(updatedEvent => {
                    this.updateEvent(updatedEvent.event);
                    this.setEventTypes(convertEventTypesToObjects(updatedEvent.eventTypes));
                    if (updatedEvent.metadata) {
                        this.metadata = updatedEvent.metadata;
                    }
                    this.changed = false;
                    return updatedEvent;
                })
                .then(fullEvent => {
                    this.urlSlugDefinedForDraft = fullEvent.event.urlSlug === "draft";
                    return fullEvent;
                });
        },

        changeEventStatus(payload: { status?: EventStatus; visibility?: Visibility }): Promise<FullEvent> {
            return events.changeEventStatus(this.eventId, payload).then(updatedEvent => {
                this.updateEvent(updatedEvent.event);
                this.setEventTypes(convertEventTypesToObjects(updatedEvent.eventTypes));
                if (updatedEvent.metadata) {
                    this.metadata = updatedEvent.metadata;
                }
                return updatedEvent;
            });
        },

        submitForReview(data: SubmitEventForReviewRequest): Promise<FullEvent> {
            return events.submitEventForReview(this.eventId, data).then(updatedEvent => {
                this.updateEvent(updatedEvent.event);
                this.setEventTypes(convertEventTypesToObjects(updatedEvent.eventTypes));
                if (updatedEvent.metadata) {
                    this.metadata = updatedEvent.metadata;
                }
                return updatedEvent;
            });
        },
    },
});
